import React, { useState, useEffect, useContext } from 'react';
import { ErpContext } from '../../context/ErpContext.js';
import { useTenantNavigation } from '../../hooks/useTenantNavigation';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  LinearProgress,
  Chip,
  IconButton,
  AppBar,
  Toolbar,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import imgLutenteNav from '../../assets/images/LutenteNavTransparent.png';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[10],
  },
}));

const ClienteDashboard = () => {
  const navigate = useTenantNavigation();
  const { getMisPaquetesCliente, verificarSesionCliente, getParametrosGlobalesCliente } = useContext(ErpContext);
  
  const [paquetes, setPaquetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clienteData, setClienteData] = useState(null);
  const [logoNavbar, setLogoNavbar] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    const cargarLogoNavbar = async () => {
      try {
        if (getParametrosGlobalesCliente) {
          const parametros = await getParametrosGlobalesCliente(['logoNavbarTenant']);
          if (parametros?.logoNavbarTenant) {
            setLogoNavbar(parametros.logoNavbarTenant);
          }
        }
      } catch (err) {
        console.error('Error al cargar logo del portal:', err);
      }
    };
    cargarLogoNavbar();
  }, [getParametrosGlobalesCliente]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError('');

      // Verificar sesión
      const sesionValida = await verificarSesionCliente();
      if (!sesionValida) {
        handleLogout();
        return;
      }

      // Obtener datos del cliente desde localStorage
      const clienteDataStr = localStorage.getItem('clienteData');
      if (clienteDataStr) {
        setClienteData(JSON.parse(clienteDataStr));
      }

      // Cargar paquetes
      const response = await getMisPaquetesCliente();
      setPaquetes(response.paquetes || []);
      
    } catch (err) {
      console.error('Error cargando paquetes:', err);
      
      if (err.response?.status === 401 || err.response?.data?.code === 'TOKEN_EXPIRED') {
        setError('Tu sesión ha expirado. Por favor, ingresá nuevamente.');
        setTimeout(() => handleLogout(), 2000);
      } else {
        setError('Error al cargar tus paquetes. Por favor, intentá nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Limpiar token y datos de cliente
    localStorage.removeItem('clienteToken');
    localStorage.removeItem('clienteData');
    // Redirigir a login
    navigate('mispagos/login', { replace: true });
  };

  const handleVerCuotas = (paquete) => {
    navigate(`mispagos/cuotas/${paquete.idItem}/${paquete.idUbicacion}`);
  };

  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(monto);
  };

  const parseFechaLocal = (fecha) => {
    if (!fecha) return null;
    if (fecha instanceof Date) return fecha;
    if (typeof fecha === 'string') {
      const match = fecha.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const [, year, month, day] = match;
        return new Date(Number(year), Number(month) - 1, Number(day));
      }
    }
    const parsed = new Date(fecha);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatearFecha = (fecha) => {
    const date = parseFechaLocal(fecha);
    if (!date) return '-';
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calcularDiasParaVencimiento = (fecha) => {
    const date = parseFechaLocal(fecha);
    if (!date) return null;
    const dias = Math.ceil((date - new Date()) / (1000 * 60 * 60 * 24));
    return dias;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Paper sx={{ p: 4, borderRadius: '16px' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Cargando tus paquetes...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: 'transparent', boxShadow: 0, color: 'text.primary' }}>
        <Toolbar sx={{ backgroundColor: 'transparent' }}>
          <img
            src={logoNavbar || imgLutenteNav}
            alt="Logo"
            style={{ height: '40px', marginRight: '16px' }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary' }}>
            Portal de Clientes
          </Typography>
          {clienteData && (
            <Typography variant="body2" sx={{ mr: 2, color: 'text.primary' }}>
              {clienteData.nombre} {clienteData.apellido}
            </Typography>
          )}
          <IconButton onClick={handleLogout} title="Cerrar sesión" sx={{ color: 'text.primary' }}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Contenido */}
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Bienvenida */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: '16px' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            ¡Bienvenido, {clienteData?.nombre}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Acá podés consultar el estado de tus paquetes y cuotas.
          </Typography>
        </Paper>

        {/* Errores */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {/* Listado de Paquetes */}
        {paquetes.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '16px' }}>
            <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No tenés paquetes registrados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Contactanos para más información
            </Typography>
          </Paper>
        ) : (
          <>
            <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
              Mis Paquetes ({paquetes.length})
            </Typography>

            <Grid container spacing={3}>
              {paquetes.map((paquete) => {
                const diasVencimiento = calcularDiasParaVencimiento(paquete.proximoVencimiento);
                const venceProximo = diasVencimiento !== null && diasVencimiento >= 0 && diasVencimiento <= 7;

                return (
                  <Grid item xs={12} md={6} lg={4} key={`${paquete.idItem}-${paquete.idUbicacion}`}>
                    <StyledCard>
                      <CardContent sx={{ flexGrow: 1 }}>
                        {/* Título */}
                        <Typography variant="h6" gutterBottom fontWeight="bold" noWrap>
                          {paquete.descripcionItem}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          📍 {paquete.descripcionUbicacion}
                        </Typography>

                        {(paquete.fechaInicio || paquete.fechaFin) && (
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                            {paquete.fechaInicio && (
                              <Chip 
                                icon={<CalendarTodayIcon />}
                                label={`Inicio: ${formatearFecha(paquete.fechaInicio)}`}
                                size="small" 
                                variant="outlined"
                              />
                            )}
                            {paquete.fechaFin && (
                              <Chip 
                                icon={<CalendarTodayIcon />}
                                label={`Fin: ${formatearFecha(paquete.fechaFin)}`}
                                size="small" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* Progreso */}
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Progreso
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {paquete.porcentajePagado}%
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={paquete.porcentajePagado} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: paquete.porcentajePagado === 100 ? 'success.main' : 'primary.main'
                              }
                            }}
                          />
                        </Box>

                        {/* Cuotas */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          <Chip 
                            icon={<CheckCircleIcon />}
                            label={`${paquete.cuotasPagadas} pagadas`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                          <Chip 
                            icon={<AccessTimeIcon />}
                            label={`${paquete.cuotasPendientes} pendientes`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        </Box>

                        {/* Montos */}
                        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Total Actualizado:
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                (con intereses)
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight="bold" color="primary.main">
                              {formatearMoneda(paquete.montoTotal)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="success.main">
                              Pagado:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatearMoneda(paquete.montoPagado)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="error.main">
                              Pendiente:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {formatearMoneda(paquete.montoPendiente)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Próximo Vencimiento */}
                        {paquete.proximoVencimiento && (
                          <Box 
                            sx={{ 
                              mt: 2, 
                              p: 1.5, 
                              bgcolor: venceProximo ? 'error.lighter' : 'info.lighter',
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <CalendarTodayIcon 
                              fontSize="small" 
                              sx={{ color: venceProximo ? 'error.main' : 'info.main' }}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="caption" display="block" color="text.secondary">
                                Próximo vencimiento
                              </Typography>
                              <Typography 
                                variant="body2" 
                                fontWeight="bold"
                                color={venceProximo ? 'error.main' : 'info.main'}
                              >
                                {formatearFecha(paquete.proximoVencimiento)}
                                {diasVencimiento !== null && diasVencimiento >= 0 && (
                                  <span style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                                    {' '}({diasVencimiento} {diasVencimiento === 1 ? 'día' : 'días'})
                                  </span>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleVerCuotas(paquete)}
                          sx={{
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 'bold',
                          }}
                        >
                          Ver Detalle de Cuotas
                        </Button>
                      </CardActions>
                    </StyledCard>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
};

export default ClienteDashboard;
